import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Globe2 } from "lucide-react";
import { FOR_PERSONAS, NETWORK_PROOF_CARDS, NP_STATUS_TONE, PEOPLE_TEASER_IMAGES } from "./constants";

export function PeopleTeaserSection() {
  return (
    <section className="container-x py-24 md:py-28">
      <div className="grid md:grid-cols-[1fr_1.2fr] gap-10 md:gap-14 items-center">
        <div>
          <p className="eyebrow">People Network</p>
          <h2 className="h-display mt-5 max-w-md">Meet the people behind the network.</h2>
          <p className="mt-6 max-w-lg text-base md:text-lg text-muted-foreground leading-relaxed">
            Behind every data point is a person. Behind every person is a household, a livelihood, a village and a future being built.
          </p>
          <Link to="/people" className="mt-7 inline-flex items-center gap-2 text-sm text-signal-lime hover:gap-3 transition-all" data-testid="home-people-cta">
            Explore the People Network <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-2 md:gap-3">
          {PEOPLE_TEASER_IMAGES.map((entry, i) => {
            const isPair = typeof entry === "object";
            const src      = isPair ? entry.src   : entry;
            const hoverSrc = isPair ? entry.hover : null;
            const alt      = isPair ? entry.alt   : "People in the Circularity network";
            // Unified motion: every tile gets the same subtle Ken-Burns rise on hover.
            // Paired tiles cross-fade B&W → color; single-image tiles drop their grayscale filter.
            const motion = "transition-transform duration-[1100ms] ease-out group-hover:scale-[1.045] group-hover:-translate-y-[2px]";
            const baseImgCls = hoverSrc
              ? `transition-opacity duration-500 group-hover:opacity-0 ${motion}`
              : `grayscale transition-[filter] duration-700 group-hover:grayscale-0 ${motion}`;
            return (
              <div
                key={src}
                className={`group relative aspect-square overflow-hidden rounded-2xl border border-border ${i % 2 === 1 ? "translate-y-4 md:translate-y-8" : ""}`}
                data-testid={`people-teaser-tile-${i}`}
              >
                <img
                  src={src}
                  alt={alt}
                  loading="lazy"
                  className={`absolute inset-0 h-full w-full object-cover ${baseImgCls}`}
                />
                {hoverSrc && (
                  <img
                    src={hoverSrc}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className={`absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100 ${motion}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function ForEveryoneSection() {
  const { t } = useTranslation();
  return (
    <section className="container-x py-24 md:py-28">
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <p className="eyebrow">{t("home.for_eyebrow")}</p>
          <h2 className="h-editorial mt-4">{t("home.for_title")}</h2>
        </div>
        <Globe2 className="text-muted-foreground" />
      </div>
      <div className="mt-10 grid gap-3 md:grid-cols-3">
        {FOR_PERSONAS.map((p) => (
          <Link key={p.to} to={p.to} data-testid={`for-card-${p.label.toLowerCase()}`} className="card-surface group">
            <div className="flex items-center justify-between">
              <p className="font-display text-xl tracking-tight">{t("home.for_prefix")} {p.label}</p>
              <ArrowUpRight size={16} className="text-muted-foreground group-hover:text-signal-lime transition-colors" />
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{t(`home.for_${p.key}_sub`)}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function NetworkProofTeaserSection() {
  return (
    <section className="container-x py-24" data-testid="home-network-proof">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow">Already forming in the real world</p>
          <h2 className="h-editorial mt-4 max-w-3xl">Not a concept slide. An active network — with honest status tags.</h2>
        </div>
        <Link to="/network-proof" className="btn-secondary" data-testid="home-np-explore">Explore Network Proof <ArrowUpRight size={14} /></Link>
      </div>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {NETWORK_PROOF_CARDS.map((c) => {
          const tone = NP_STATUS_TONE[c.s] || "text-signal-amber";
          return (
            <Link key={c.id} to={`/network-proof/${c.id}`} className="card-surface group" data-testid={`home-np-${c.id}`}>
              <div className="flex items-center justify-between">
                <p className="eyebrow">{c.t}</p>
                <span className={`font-mono text-[10px] uppercase tracking-[0.18em] ${tone}`}>{c.s.replace(/_/g, " ")}</span>
              </div>
              <h3 className="mt-3 font-display text-lg tracking-tight">{c.b}</h3>
              <p className="mt-3 text-xs text-muted-foreground">Open the page → status, sources, role.</p>
            </Link>
          );
        })}
      </div>
      <p className="mt-6 text-[11px] text-muted-foreground">
        Trust principle: every claim is sourced, timestamped, and tagged with confidence. We don&apos;t overstate relationships. <Link to="/methodology" className="text-signal-lime hover:underline">See methodology →</Link>
      </p>
    </section>
  );
}
